import React, { useCallback } from "react";
import { gql, useQuery } from "@apollo/client";
import {
  CustomerPointsHistoryData,
  CustomerPointsHistoryQueryQuery,
  CustomerPointsHistoryQueryQueryVariables,
} from "../../../../../schemaTypes";

export const customerPointsHistoryQuery = gql`
  query CustomerPointsHistoryQuery(
    $userId: String!
    $page: Int!
    $pageSize: Int!
    $actionFilter: String
    $categoryFilter: String
    $typeFilter: String
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    customerPointsHistory(
      userId: $userId
      page: $page
      pageSize: $pageSize
      actionFilter: $actionFilter
      categoryFilter: $categoryFilter
      typeFilter: $typeFilter
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        createdAt
        type
        action
        category
        pointBalance
        tierBonus
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithCustomerPointsHistory {
  data: CustomerPointsHistoryData[];
  loading: boolean;
  loadMore: () => void;
  hasMoreTransactions: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface Props {
  variables: CustomerPointsHistoryQueryQueryVariables;
  children: (data: WithCustomerPointsHistory) => React.ReactElement | null;
}

export const CustomerPointsHistoryController: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    CustomerPointsHistoryQueryQuery,
    CustomerPointsHistoryQueryQueryVariables
  >(customerPointsHistoryQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const customerPointHistory = data?.customerPointsHistory;

  const customerPointHistoryData = customerPointHistory?.data || [];
  const page = customerPointHistory?.page ?? variables.page;
  const pageSize = customerPointHistory?.pageSize ?? variables.pageSize;
  const total = customerPointHistory?.total ?? 0;
  const totalPages = customerPointHistory?.totalPages ?? 0;

  const hasMoreTransactions = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreTransactions) return;

    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.customerPointsHistory) {
          return prev;
        }

        return {
          customerPointsHistory: {
            ...fetchMoreResult.customerPointsHistory,
            data: [
              ...(prev.customerPointsHistory?.data || []),
              ...(fetchMoreResult.customerPointsHistory.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMoreTransactions]);

  return children({
    data: customerPointHistoryData,
    loading,
    loadMore,
    hasMoreTransactions,
    page,
    pageSize,
    total,
    totalPages,
  });
};